import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";
import type { components } from "../api/generated/survey-svc";
import type { AuthenticatedUser } from "../domain/authenticated-user/model";
import { apiMe } from "../services/IndicafixAuthService";
import type { SessionSnapshot, SessionUserUpdate } from "./session";
import { createSessionController, setUnauthorizedHandler } from "./session";

export type SessionContextValue = {
  status: "restoring" | "anonymous" | "authenticated";
  ready: () => Promise<SessionSnapshot>;
  getSnapshot: () => SessionSnapshot;
  token: string | null;
  user: AuthenticatedUser | null;
  pendingEmail: string;
  authenticate: (token: string, user: AuthenticatedUser) => void;
  setPendingEmail: (email: string) => void;
  updateUser: (
    update: SessionUserUpdate,
    expectedToken?: string | null,
  ) => void;
  clear: () => void;
  subscribe: (listener: () => void) => () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);
const anonymousSnapshot: SessionSnapshot = {
  status: "anonymous",
  token: null,
  user: null,
  pendingEmail: "",
};
const anonymousSession: SessionContextValue = {
  ...anonymousSnapshot,
  ready: async () => anonymousSnapshot,
  getSnapshot: () => anonymousSnapshot,
  authenticate: () => undefined,
  setPendingEmail: () => undefined,
  updateUser: () => undefined,
  clear: () => undefined,
  subscribe: () => () => undefined,
};

export function SessionProvider({ children }: { children: ReactNode }) {
  const controller = useMemo(
    () =>
      createSessionController({
        restoreUser: async (): Promise<
          components["schemas"]["AuthMe"] | undefined
        > => {
          const result = await apiMe();
          if (result.status === "success" && result.data) {
            return result.data as components["schemas"]["AuthMe"];
          }
          if (result.status === "http-error" && result.statusCode === 401) {
            return undefined;
          }
          throw result.status === "network-error"
            ? result.error
            : new Error(`Session restoration failed with ${result.statusCode}`);
        },
      }),
    [],
  );
  const getSnapshot = useCallback(() => controller.snapshot, [controller]);
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    getSnapshot,
    getSnapshot,
  );

  useEffect(() => {
    setUnauthorizedHandler(controller.clearIfToken);
    void controller.restore();
    return () => setUnauthorizedHandler(undefined);
  }, [controller]);

  const value = useMemo<SessionContextValue>(
    () => ({
      ...snapshot,
      ready: controller.restore,
      getSnapshot,
      authenticate: controller.authenticate,
      setPendingEmail: controller.setPendingEmail,
      updateUser: controller.updateUser,
      clear: controller.clear,
      subscribe: controller.subscribe,
    }),
    [controller, snapshot, getSnapshot],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession() {
  const session = useContext(SessionContext);
  return session ?? anonymousSession;
}

export function useAuthUser() {
  const { user } = useSession();
  return useMemo(
    () =>
      user
        ? {
            id: user.id,
            name: user.name,
            email: user.email,
            jobRole: user.jobRole,
            phone: user.phone,
            role: user.role,
            profileImg: user.image || null,
            companyId: user.company?.id ?? "",
            companyName: user.company?.name ?? "",
            companyLogo: user.company?.image || null,
            companyPaid: user.company?.paid ?? false,
            companyRole: user.companyRole,
            language: user.language,
          }
        : {
            id: "",
            name: "",
            email: "",
            jobRole: "",
            phone: "",
            role: "",
            profileImg: null,
            companyId: "",
            companyName: "",
            companyLogo: null,
            companyPaid: false,
            companyRole: null,
            language: "",
          },
    [user],
  );
}
